//
// forms/submission.js - form submission controller
//
// - Subscribes to submission & value changes
// - Validates data
// - Submits leads using api.js
//
// - Sends events on the form element (these events should be used to trigger UI updates only):
//    - Form/ValidityUpdate
//    - Form/BeginSubmission
//    - Form/CancelSubmission
//    - Form/EndSubmission
//
// - You can register participants in the submit process:
//    - success:  (form) => null                 - main intended use is for tracking integrations
//    - failure:  (form) => null                 - "
//    - submit:   (form, response_obj) => null   - allows replacing the form submission API call entirely! caution!
//    e.g:
//        const controller = submission(form_element);
//        controller.register__on_success(_ => alert('Hooray!!));
//
// - Note: - is automatically instantiated by js-mkto-form on all regular forms
//
//         - for more custom forms you can instantiate a controller manually, i.e.:
//              const controller = submission(form_element)
//
//         - custom forms requirements:
//              - form element must have .js-form-outer
//              - form items must:
//                   - be wrapped in a div.js-form-item
//                   - have data-type="email" or similar
//              - form inputs must have a `name` and where appropriate a `type`
//              - ui updates should be handled by subscribing to the Form/* events on
//                the form element
//
//           (NB to prevent followup actions on the form, see js_callback)

import foreach from '../helpers/object-foreach';
import { curry, chain } from '../helpers/functional';
import * as collection_listener from '../helpers/collection-listener';
import find_ancestor from '../helpers/find-ancestor';
import { get_cookie } from '../cookies/crumblr';

import * as helpers from './helpers';
import * as api from './api';
import * as validity from './validity';
import * as value_extractor from './value-extractor';
import { data_store } from './data-store';

const store = data_store();
const ENTER_KEY = 13;

// validation
// ----------------------------------

// - Validate an input:
//   - when input has been changed and then blurred, or
//   - when form has previously been submitted

function should_validate_field(el_input, form) {
  return form.has_submitted || (el_input.has_changed && el_input.has_blurred);
}

function cb_field_change(ev) {
  const el_input = ev.currentTarget;
  const form = get_form(el_input);

  el_input.has_changed = true;

  if (should_validate_field(el_input, form)) {
    validate_single_field(el_input);
  }
}

function cb_keyup(ev) {
  if (ev.which === ENTER_KEY) {
    return;
  }
  cb_field_change(ev);
}

function validate_single_field(el_input) {
  const el_div = find_ancestor(el_input, 'js-form-item');
  if (!el_div) {
    throw Error('validate_single_field(): parent div not found for element');
  }

  const vdc = validity.get_validation_data_collection([el_input]);
  if (vdc.length !== 1 || vdc[0][0] !== el_div) {
    throw Error(
      'validate_single_field(): unexpected result from validity.get_validation_data_collection',
    );
  }

  dispatch_validity_update(get_form(el_div), vdc);
}

function cb_field_blur(ev) {
  const el_input = ev.currentTarget;
  el_input.has_blurred = true;

  if (should_validate_field(el_input, get_form(el_input))) {
    validate_single_field(el_input);
  }
}

// cb_loose_validate
// ----------------------------------
// - validate filled out email, phone fields

function cb_loose_validate(ev) {
  const form = ev.detail ? ev.detail : ev.target;
  const fields_to_validate = Array.from(form.elements)
    .filter((item) => ['email', 'tel'].includes(item.getAttribute('type')))
    .filter((item) => item.value.length > 0);

  fields_to_validate.forEach(validate_single_field);
}

// event dispatch
// ----------------------------------

function dispatch_validity_update(form, vdc) {
  dispatch_event(form, 'ValidityUpdate', vdc);
}

function dispatch_event(form, event_name, state) {
  form.dispatchEvent(new CustomEvent(`Form/${event_name}`, { detail: state }));
}

// fill_cookie_driven_field()
// ----------------------------------
// - Some hidden fields take their values from cookies / localStorage / etc.
// - NB only 'simple' field types are supported (i.e. supporting el.value = X)

function fill_cookie_driven_field(el_input) {
  const name = el_input.getAttribute('name');
  const source = el_input.getAttribute('data-cookie-source');
  let value;

  if (!source || name === 'Google_Click_ID_gclid__c') {
    return;
  }

  // 'bwurl' field -> from document location
  if (
    name === 'bwurl'
    || name === 'bw_url'
    || source === 'bwurl'
    || source === 'bw_url'
  ) {
    value = remove_params(window.location.href);
  }

  // fv_ or utm_ -> from sessionStorage
  else if (source.match(/^(fv_|utm_)/) && store !== null) {
    value = store.get_for_session(source);
  }

  // regular cookie-driven fields
  else {
    value = get_cookie(source);
  }

  el_input.value = value;
}

function remove_params(url) {
  const [path] = url.split('?');
  return path;
}

function fill_cookie_driven_fields(form) {
  Array.from(
    form.querySelectorAll('input[type="hidden"][data-cookie-source]'),
  ).forEach(fill_cookie_driven_field);
}

// get_form_data()
// ----------------------------------
// -> { name : value, ... }

async function get_form_data(form) {
  const return_null_for_empty_values = [
    'Email_Events_Webinars__c',
    'Email_Insights__c',
    'Email_Newsletter__c',
    'Email_Product_Updates__c',
    'GDPR_Data_Rights__c',
    'Unsubscribed',
  ];

  const form_values_obj = value_extractor.get_form_data(form.elements);

  const form_data = Object.keys(form_values_obj).reduce((carry, item_name) => {
    const item = form_values_obj[item_name];

    if (item.value === '' && return_null_for_empty_values.includes(item_name)) {
      carry[item_name] = 'NULL';
    }
    else {
      carry[item_name] = item.value;
    }

    return carry;
  }, {});

  // Some ancillary form data
  if (!form.local_endpoint) {
    const form_id = form.getAttribute('data-form-id');
    if (!form_id || !form_id.match(/^\d+(-falcon)?$/)) {
      throw 'invalid form id';
    }

    form_data.formid = form_id;
    form_data.formVid = form_id;

    form_data._mkt_trk = get_cookie('_mkto_trk'); // NB Cookie has the 'o'; form param doesn't
  }

  return form_data;
}

// save_prefill_data()
// ----------------------------------

function should_save_as_prefill_item(item_name) {
  const exclude_prefixes = [
    '_mkt_trk',
    'utm_',
    'fv_',
    'bwsitegeo',
    'bwurl',
    'formid',
    'formvid',
    'websiteindustry',
    'google',
  ];

  const item_name__lower = item_name.toLowerCase();
  return (
    exclude_prefixes.find((excl) => item_name__lower.indexOf(excl) === 0)
    === undefined
  );
}

function save_prefill_data(form, form_data) {
  if (!store) {
    return;
  }

  foreach(form_data, (item_name, item) => {
    if (should_save_as_prefill_item(item_name)) {
      store.set(item_name, item);
    }
  });
}

// decode_response
// ----------------------------------

async function decode_response(response) {
  let response_obj = null;

  if (typeof response !== 'object') {
    try {
      response_obj = JSON.parse(response);
    }
    catch (exc) {
      throw 'invalid json response';
    }
  }
  else {
    response_obj = response;
  }

  // If a BUFFS response, check for failure
  if (response_obj.buffs && response_obj.buffs.success !== true) {
    throw `buffs: ${response_obj.buffs.msg}`;
  }

  return response_obj;
}

// run_success_processes()
// ----------------------------------

async function run_success_processes(form, response_obj) {
  let delay = false;
  const success_procs = form.submit_processes.success;

  try {
    success_procs.forEach((f) => {
      const opts = f.call(null, form, response_obj) || {};
      delay = !!(delay || (opts && opts.hasOwnProperty('delay') && opts.delay));
      // Success processes can return { delay: true } to set a delay after running
    });
  }
  catch (err) {
    console.log(
      'forms/submission.js: submit__success: a success submit process failed',
      err,
    );
    // Consider submit processes relatively unimportant (largely marketing integrations) -- so that
    // a failure doesn't fully disrupt the user's experience of form submission, just continue
  }

  if (delay) {
    return new Promise((y) => setTimeout((y) => true), 500);
  }
}

// js_callback()
// ----------------------------------
// Custom JS callback:
// - takes response_obj, which it can modify
//
// - to specify a redirect, should just set response_obj.followup_actions.redirect
//
// - or, can take care of business itself, and prevent any other redirect that may
//   exist on the form by setting response_obj.followup_actions.prevent_all = true

function js_callback(form, response_obj) {
  function get_callback() {
    const cb_name = form.getAttribute('data-callback');
    return cb_name && typeof window[cb_name] === 'function'
      ? window[cb_name]
      : null;
  }

  const fn = get_callback();
  if (fn) {
    fn(response_obj);
  }
}

// redirect
// ----------------------------------

function do_redirect(redirect) {
  const is_absolute = redirect.match(/(^https?:\/\/)|(^\/)/);
  if (!is_absolute) {
    redirect = window.location.pathname + redirect;
  }
  window.location.href = redirect;
}

// cb_submit() - submit form
// ----------------------------------

async function cb_submit(form, ev) {
  window.onbeforeunload = function () {
    return 'Are you sure you want to navigate away? Changes you have made will not be saved.';
  };

  if (form.submitting) {
    return;
  }

  form.submitting = true;
  form.has_submitted = true;
  let buffs_invoked = false;

  try {
    // Validate user input
    const vdc = validity.get_validation_data_collection(form.elements);
    dispatch_validity_update(form, vdc);
    if (!vdc.all_ok) {
      throw 'invalid form data';
    }

    dispatch_event(form, 'BeginSubmission');

    // Get and save form data
    fill_cookie_driven_fields(form);
    const form_data = await get_form_data(form);
    save_prefill_data(form, form_data);

    // Submit:
    //  - via api.js to Marketo/BUFFS, OR
    //  - via a 'submit process' if registered
    let response;
    if (form.submit_processes.submit.length > 0) {
      response = await form.submit_processes.submit[0](form, form_data);
      // (only 1 submit process currently supported)
    }
    else if (form.local_endpoint) {
      response = await api.submit__local(form.local_endpoint, form_data);

      reset_form(form);
    }
    else {
      try {
        response = await api.submit(form_data);
      }
      catch (err) {
        buffs_invoked = true;
        response = await api.submit__buffs(form_data); // NB: if BUFFS also fails, will throw nayjax error
      }
    }

    // Get response:
    const response_obj = await decode_response(response);
    response_obj.followup_actions = {
      redirect: null, // The js callback can set this to a location to tell us to perform a redirect
      prevent_all: false, // The js callback can set this to true to override ALL actions on the form,
      // including redirect set as data-redirect opt in the form template
      get_redirect() {
        return helpers.get_redirect(form, response_obj, form_data);
      },
    };
    dispatch_event(form, 'EndSubmission');
    form.submitting = false;

    window.onbeforeunload = null;

    // Followup actions (use response_obj.followup_actions to modify):
    run_success_processes(form, response_obj);
    js_callback(form, response_obj);
    const redirect = response_obj.followup_actions.get_redirect();
    if (redirect) {
      do_redirect(redirect);
    }
  }
  catch (err) {
    form.submitting = false;
    dispatch_event(form, 'CancelSubmission');
    console.log('forms/submission.js: submit error:', err);

    if (err === 'invalid form data') {
      // ValidityUpdate event was already sent
    }
    else {
      alert(
        'An error occurred during form submission.\n\n'
          + 'We’re really sorry about this; please try again, or get in touch and we’ll help you out.\n\n'
          + `(Please provide this error message: '${err}${
            buffs_invoked ? ' type b' : ''
          }')`,
      );
    }

    form.submit_processes.failure.forEach((f) => f.call(null, form, err));
  }
}

// get_form(inner_form_element)
// ----------------------------------

function get_form(inner_form_element) {
  return find_ancestor(inner_form_element, 'js-form-outer');
}

// reset_form
// ----------------------------------

function reset_form(form) {
  form.reset();

  const elementWrappers = document.querySelectorAll('.c-form-set__inner');

  elementWrappers.forEach((elementWrapper) => {
    elementWrapper.classList.remove('s-validation-ok', 's-validation-error');
  });
}

// init
// ----------------------------------

function prevent(ev) {
  ev.preventDefault();
}

function init(form) {
  if (form.tagName !== 'FORM') {
    throw Error('Initialised on non-form element', 'forms/submission.js');
  }

  form.submit_cb = chain(prevent, curry(cb_submit, form));
  form.addEventListener('submit', form.submit_cb);

  const elements = Array.from(form.elements).filter(
    (el) => el.tagName !== 'BUTTON',
  );

  collection_listener.add(elements, 'change', cb_field_change);
  collection_listener.add(elements, 'keyup', cb_keyup);
  collection_listener.add(elements, 'blur', cb_field_blur);

  form.classList.remove('c-form--loading');
  form.onsubmit = null;

  form.submit_processes = {
    success: [],
    failure: [],
    submit: [],
  };

  const local_endpoint = form.getAttribute('data-local-endpoint');
  form.local_endpoint = local_endpoint;

  function register_process(stage, f) {
    form.submit_processes[stage].push(f);
  }

  form.addEventListener('Form/LooseValidate', cb_loose_validate);

  return {
    register__on_success: curry(register_process, 'success'), // callback(form, response_obj) -> null
    register__on_failure: curry(register_process, 'failure'), // callback(form, err)          -> null
    register__submit: curry(register_process, 'submit'), // cb(form_data)    -> promise(stringified json)
  };
}

export default function submission_controller(form) {
  if (!form.__submission_ctrlr) {
    form.__submission_ctrlr = init(form);
  }

  return form.__submission_ctrlr;
}
